// ink-component
import React, { ReactNode } from 'react';
import { ThemeProvider } from 'styled-components';
import Ornament, { OrnamentProps } from '../Ornament/Ornament';
import Text from '../Typography/Text/Text';
import getGlobalProps from '../../helpers/constants/global-props';
import { MiniStatus, StatusInfo, StatusLabel, StyledStatus, OrnamentWrapper } from './styles';
import { FeedbackVariants, OrnamentVariantColorTypes } from '../_common/Types/GlobalTypes';
import { IconTypes } from '../../helpers/constants/icons';

export interface StatusProps {
  /** Semantic variants, based on intent. Use `feedback-neutral` and
   * `feedback-positive` for binary to-dos. Use `feedback-pending`,
   * `feedback-notice`, and `feedback-negative` for intermediary states in a
   * progression. [Read more about Status variants](/components/Status/usage).
   * */
  variant?: Extract<
    FeedbackVariants,
    | 'feedback-positive'
    | 'feedback-negative'
    | 'feedback-notice'
    | 'feedback-neutral'
    | 'feedback-pending'
    | 'feedback-loading'
  >;
  /** Modifier for different ornament sizes */
  size?: OrnamentProps['size'];
  /** Right side text for mini status */
  label?: ReactNode;
  /** Right side text for standard status */
  primaryText?: ReactNode;
  /** Right side text for standard status */
  secondaryText?: ReactNode;
  /** Modifier for mini status */
  isMini?: boolean;
  /** Improves alignment inside table cells */
  isCell?: boolean;
  /** Modifier for Statuses with no icons */
  noIcon?: boolean;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
  /** @deprecated:variant This prop is deprecated and should not be used. */
  isInverse?: boolean;
  /** @deprecated:variant This prop is deprecated and should not be used. */
  isDashed?: boolean;
  /** @deprecated:variant This prop is deprecated and should not be used. */
  isSpinner?: boolean;
  /** @deprecated:variant This prop is deprecated and should not be used. */
  color?: Exclude<OrnamentVariantColorTypes, 'brown'>;
  /** @deprecated:variant This prop is deprecated and should not be used. */
  icon?: IconTypes;
}

/**
 * @purpose Visually indicates status
 * @categories layout
 */
function Status(props: StatusProps) {
  const {
    noIcon,
    isMini,
    isCell,
    isDashed,
    isInverse,
    primaryText,
    secondaryText,
    label,
    variant,
    size,
    isSpinner,
    'data-testid': dataTestId,
    ...otherProps
  } = props;

  let globalProps = {};

  if (isDashed || isInverse || isSpinner || otherProps.color || otherProps.icon) {
    globalProps = getGlobalProps({ deprecatedProps: true });
  }

  if (!variant && otherProps.color) {
    console.warn(
      '[INK DEPRECATION WARNING]: The `color` prop in Status is deprecated. Use `variant` instead. https://ink.carta.com/components/Status/usage',
    );
  }

  if (!variant && otherProps.icon) {
    console.warn(
      '[INK DEPRECATION WARNING]: The `icon` prop in Status is deprecated. Use `variant` instead. https://ink.carta.com/components/Status/usage',
    );
  }

  if (isInverse) {
    console.warn(
      '[INK DEPRECATION WARNING]: The `isInverse` prop in Status is deprecated. Use `variant` instead. https://ink.carta.com/components/Status/usage',
    );
  }

  if (isDashed) {
    console.warn(
      '[INK DEPRECATION WARNING]: The `isDashed` prop in Status is deprecated. Use `variant` instead. https://ink.carta.com/components/Status/usage',
    );
  }

  if (isSpinner) {
    console.warn(
      '[INK DEPRECATION WARNING]: The `isSpinner` prop in Status is deprecated. Use `variant` instead. https://ink.carta.com/components/Status/usage',
    );
  }

  if (variant && (otherProps.color || otherProps.icon || isDashed || isInverse || isSpinner)) {
    throw new Error(`[_ink] Deprecated props should not be used with 'variant' in Status`);
  }

  const hasIcon = !noIcon && (otherProps.icon || variant || otherProps.color);

  let color: StatusProps['color'];
  let icon: StatusProps['icon'];

  if (!variant) {
    color = otherProps.color || 'blue';
    icon = otherProps.icon || 'right-arrow';
  }

  return (
    <ThemeProvider
      theme={currentTheme => ({
        ...currentTheme,
        statusCell: isCell,
        statusVariant: variant,
        statusSize: size,
        statusMini: isMini,
        statusHasSecondaryText: Boolean(secondaryText),
        ...(!variant && {
          statusColor: color,
          statusIcon: icon,
          statusInverse: isInverse,
          statusDashed: isDashed,
        }),
      })}
    >
      <StyledStatus {...globalProps} data-testid={dataTestId}>
        {hasIcon &&
          (isMini ? (
            <MiniStatus />
          ) : (
            <OrnamentWrapper>
              <Ornament
                color={variant ? undefined : color}
                icon={variant ? undefined : icon}
                isDashed={isDashed}
                isInverse={isInverse}
                isSpinner={isSpinner}
                size={size}
                variant={variant}
              />
            </OrnamentWrapper>
          ))}
        {label && isMini && <StatusLabel>{label}</StatusLabel>}
        {!!primaryText && (
          <StatusInfo>
            <Text variant="body-2" trim>
              {primaryText}
            </Text>
            {!!secondaryText && (
              <Text variant="body-3-feedback-neutral" trim>
                {secondaryText}
              </Text>
            )}
          </StatusInfo>
        )}
      </StyledStatus>
    </ThemeProvider>
  );
}

Status.defaultProps = {
  isMini: false,
  isInverse: false,
  isDashed: false,
  isCell: false,
  noIcon: false,
  isSpinner: false,
  size: 'large',
};

Status.displayName = 'Status';

export default Status;
